'use strict';

import { documentReady, arraysAreEqual } from 'Util/util';

export default class VZPlpProductTile extends HTMLElement {
    constructor() {
        super();
    }

    /**
     * Invoked when the element is added to the DOM.
     * Sets up event listeners and initializes the component's state.
     */
    connectedCallback() {
        this.plpSidebar = document.querySelector('vz-plp-sidebar');
        this.plpBasket = JSON.parse(this.plpSidebar.getAttribute('plp-basket'));

        this.productId = this.getAttribute('product-id');
        this.productName = this.getAttribute('product-name');
        this.productSalePrice = this.getAttribute('product-sale-price');
        this.productListPrice = this.getAttribute('product-list-price');
        this.oneOffPrice = this.getAttribute('product-one-off-price');

        this.dependencyName = this.getAttribute('product-to-add-name');
        this.dependencySalePrice = this.getAttribute('product-to-add-sale-price');
        this.dependencyOneOffPrice = this.getAttribute('product-to-add-one-off-price');
        this.productDependency = this.getAttribute('product-dependency') !== 'null' ? this.getAttribute('product-dependency') : null;
        this.dependencyLabel = this.querySelector('.js-dependency-label');
        this.isDependent = false;

        this.productExcluded = this.getAttribute('product-excluded');
        this.excludedLabel = this.querySelector('.js-excluded-label');
        this.isProductExcluded = false;
        this.productExcludedTile = this.productExcluded ? document.querySelector(`.js-${this.productExcluded}-product-tile`) : null;
        this.productExcludedLabel = this.productExcludedTile?.querySelector('.js-excluded-label');

        this.qtySelectorWrapper = this.querySelector('vz-plp-quantity-selector');
        this.selectorContainer = this.querySelectorAll('.js-selector-container');
        this.qty = this.qtySelectorWrapper?.querySelector('.js-quantity-field')?.value;

        this.toggle = this.querySelector('.js-plp-toggle');
        this.isSelected = this.toggle?.checked;

        this.isProgrammaticUpdate = false;

        this.setAttribute('qty', this.qty);
        this.setAttribute('is-selected', this.isSelected);

        this.setCurrentState = this.setCurrentState.bind(this);
        this.setQty = this.setQty.bind(this);
        this.setIsSelected = this.setIsSelected.bind(this);
        this.updateBasket = this.updateBasket.bind(this);

        document.addEventListener('plp:basketUpdated', this.setCurrentState);
        this.qtySelectorWrapper?.addEventListener('plp:tileUpdated', this.setQty);
        this.qtySelectorWrapper?.addEventListener('plp:qtySelectorReady', this.setCurrentState);
        this.toggle?.addEventListener('change', this.setIsSelected);
        documentReady(this.setCurrentState);
    }

    /**
     * @function disconnectedCallback
     * @description Removes event listeners when the component is disconnected from the DOM
     * @returns {void}
     */
    disconnectedCallback() {
        document.removeEventListener('plp:basketUpdated', this.setCurrentState);
        document.removeEventListener('plp:tileUpdated', this.setQty);
        this.qtySelectorWrapper?.removeEventListener('plp:qtySelectorReady', this.setCurrentState);
        this.toggle?.removeEventListener('change', this.setIsSelected);
    }

    /**
     * @function observedAttributes
     * @description observes the mentioned attribute and fires attributeChangedCallback when the value has changed
     * @returns {Attr} this attribute's value is an encoded JSON string so should be decoded when using.
     */
    static get observedAttributes() {
        return ['is-selected', 'qty'];
    }

    /**
     * @function attributeChangedCallback
     * @description fired when an observed attribute changes
     * @param {string} name the name of the attribute
     * @param {any} oldValue the previous value of the attribute
     * @param {any} newValue the new value of the attribute
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (
            !this.isProgrammaticUpdate &&
            ((name === 'is-selected' && oldValue !== newValue) || (name === 'qty' && oldValue !== newValue)) &&
            oldValue !== 'initial' &&
            newValue !== 'initial'
        ) {
            this.updateBasket();
        }
    }

    /**
     * Updates the basket data based on the component's state.
     */
    updateBasket() {
        let updatedBasket = JSON.parse(this.plpSidebar.getAttribute('plp-basket'));
        updatedBasket = updatedBasket.filter((item) => item.id !== this.productId && item.id !== this.productDependency);

        if (parseInt(this.qty, 10) > 0 || this.isSelected) {
            const productObj = {
                id: this.productId,
                price: this.productSalePrice,
                startingPrice: this.productListPrice,
                oneOffPrice: this.oneOffPrice,
                name: this.productName,
                qty: this.isSelected ? 1 : this.qty,
                dependency: this.productDependency,
                isDependent: this.isDependent,
                dependsOn: null,
                excluded: this.productExcluded,
                isProductExcluded: this.productExcluded
            };

            updatedBasket.push(productObj);

            if (this.productDependency && this.productDependency !== 'null' && this.productDependency !== null) {
                const dependencyProduct = {
                    id: this.productDependency,
                    price: this.dependencySalePrice,
                    oneOffPrice: this.dependencyOneOffPrice,
                    name: this.dependencyName,
                    qty: 1,
                    dependency: null,
                    isDependent: true,
                    dependsOn: this.productName,
                    excluded: null,
                    isProductExcluded: false
                };

                updatedBasket.push(dependencyProduct);
            }
        }

        // No replacement if updated and current basket have same products
        if (arraysAreEqual(updatedBasket, this.plpBasket)) {
            return;
        }

        this.plpBasket = updatedBasket;
        this.plpSidebar.setAttribute('plp-basket', JSON.stringify(this.plpBasket));

        document.dispatchEvent(new CustomEvent('plp:basketUpdated'));
    }

    /**
     * Updates the quantity value based on the quantity selector input.
     */
    setQty() {
        this.qty = this.qtySelectorWrapper?.querySelector('.js-quantity-field')?.value;
        this.setAttribute('qty', this.qty);
    }

    /**
     * Updates the "isSelected" state based on the toggle input.
     */
    setIsSelected() {
        this.isSelected = this.toggle?.checked;
        this.setAttribute('is-selected', this.isSelected);

        // Update aria-live region for screen readers
        const stateTextElement = this.querySelector('.js-toggle-state-text');
        if (stateTextElement) {
            const yesText = this.querySelector('.custom-switch__state-yes')?.textContent;
            const noText = this.querySelector('.custom-switch__state-no')?.textContent;
            stateTextElement.textContent = this.isSelected ? yesText : noText;
        }
    }

    /**
     * Sets the current state of the component based on basket data and other attributes.
     */
    setCurrentState() {
        this.plpBasket = JSON.parse(this.plpSidebar.getAttribute('plp-basket'));
        const currentItemInBasket = this.plpBasket.filter((item) => item.id === this.productId);
        this.isProgrammaticUpdate = true;

        if (currentItemInBasket.length > 0) {
            this.isDependent = currentItemInBasket[0].isDependent;
            this.isProductExcluded = currentItemInBasket[0].isProductExcluded;

            if (currentItemInBasket[0].dependsOn) this.dependencyLabel.querySelector('strong').textContent = currentItemInBasket[0].dependsOn;

            if (this.qtySelectorWrapper) {
                const qty = parseInt(currentItemInBasket[0].qty, 10);
                const inputField = this.qtySelectorWrapper.querySelector('input');
                inputField.value = qty.toFixed();
                inputField.setAttribute('value', qty.toFixed());
                this.qtySelectorWrapper.dispatchEvent(new CustomEvent('plp:setQty'));
            } else {
                const oldVal = this.toggle.checked;
                this.toggle.checked = true;
                if (oldVal !== this.toggle.checked) {
                    this.toggle?.dispatchEvent(new CustomEvent('change'));
                }
            }
        } else if (this.qtySelectorWrapper) {
            this.isDependent = false;
            this.isProductExcluded = false;
            const inputField = this.qtySelectorWrapper.querySelector('input');
            inputField.value = 0;
            inputField.setAttribute('value', 0);
            this.qtySelectorWrapper.dispatchEvent(new CustomEvent('plp:setQty'));
        } else {
            this.isDependent = false;
            this.isProductExcluded = false;
            const oldVal = this.toggle.checked;
            this.toggle.checked = false;
            if (oldVal !== this.toggle.checked) {
                this.toggle?.dispatchEvent(new CustomEvent('change'));
            }
        }

        this.isProgrammaticUpdate = false;

        Array.from(this.selectorContainer).forEach((el) => el.classList.toggle('d-none', this.isDependent));

        this.productExcludedTile?.classList.toggle('disabled', !!this.isProductExcluded);

        this.dependencyLabel?.classList.toggle('d-none', !this.isDependent);
        this.productExcludedLabel?.classList.toggle('d-none', !this.isProductExcluded);
    }
}

if (!window.customElements.get('vz-plp-product-tile')) {
    window.customElements.define('vz-plp-product-tile', VZPlpProductTile);
}
